import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  downloadAdminSessionVideo,
  fetchAdminSessionVideo,
  parseApiErrorDetail,
  copyAdminSessionVideoUrl,
} from "../../lib/adminVideo";
import { isAdminSessionExpiredError } from "../../lib/adminAuth";
import { prepareRecordingBlob } from "../../lib/fixRecordingBlob";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  RefreshCw,
  Video,
  Clapperboard,
  Download,
  Search,
  RotateCcw,
  Trash2,
  Plus,
  Link2,
  Send,
} from "lucide-react";
import {
  canResetSessionCode,
  getSessionDisplayStatus,
  isNewRegistrationRow,
  shouldShowSessionStats,
  formatSessionLostLabel,
  getSessionResetCount,
  SESSION_DISPLAY_LABELS,
} from "../../lib/sessionDisplayStatus";

const POLL_MS = 20_000;
const LETTERS = ["A", "B", "C", "D"];
const normalizeAccessCode = (raw) => String(raw || "").replace(/\s+/g, "").toUpperCase();
const formatMoney = (n) => new Intl.NumberFormat("fr-FR").format(n || 0) + " €";

const sessionHasVideo = (s) =>
  Boolean(s.video_composite_storage_path || s.video_storage_path);

const sessionVideoVariant = (s) =>
  s.video_composite_storage_path ? "composite" : "camera";

const sessionRowId = (row) => row?.session_id || row?.id;

const STATUS_DOT = {
  new: "bg-sky-400",
  finished: "bg-emerald-400",
  recording: "bg-amber-400",
  consumed: "bg-white/35",
};

const WEBHOOK_PILL = {
  ok: "text-emerald-300 bg-emerald-500/15",
  error: "text-red-300 bg-red-500/15",
  skipped: "text-white/45 bg-white/5",
};

const WEBHOOK_LABELS = {
  ok: "OK",
  error: "Échec",
  skipped: "Off",
};

const cell = "px-3 py-2 text-[13px] leading-tight border-b border-white/[0.06] border-r border-white/[0.04] last:border-r-0";
const headCell =
  "px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/45 border-b border-white/10 border-r border-white/[0.06] last:border-r-0 bg-[#0d1520] sticky top-0 z-10 whitespace-nowrap";

const SessionsTab = ({ isActive = true, isAdminRole = false }) => {
  const [sessions, setSessions] = useState([]);
  const [videoSessionId, setVideoSessionId] = useState(null);
  const [videoPlaybackVariant, setVideoPlaybackVariant] = useState("composite");
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [videoFilter, setVideoFilter] = useState("all");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [teamContacts, setTeamContacts] = useState([]);
  const [contactSavingId, setContactSavingId] = useState(null);
  const [webhookResendingId, setWebhookResendingId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const videoObjectUrlRef = useRef(null);

  const revokeVideoObjectUrl = useCallback(() => {
    if (videoObjectUrlRef.current) {
      URL.revokeObjectURL(videoObjectUrlRef.current);
      videoObjectUrlRef.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sessions");
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (err) {
      setSessions([]);
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur de chargement des sessions");
    }
  }, []);

  const loadTeamContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/game-settings");
      setTeamContacts(Array.isArray(data?.team_contacts) ? data.team_contacts : []);
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
    }
  }, []);

  useEffect(() => {
    refresh();
    loadTeamContacts();
  }, [refresh, loadTeamContacts]);

  useEffect(() => {
    if (!isActive) return undefined;
    loadTeamContacts();
  }, [isActive, loadTeamContacts]);

  useEffect(() => {
    if (!isActive) return undefined;
    const poll = () => {
      if (document.visibilityState === "hidden") return;
      refresh();
    };
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [isActive, refresh]);

  const videoSession = sessions.find((s) => sessionRowId(s) === videoSessionId);

  useEffect(() => {
    if (!videoSessionId) {
      revokeVideoObjectUrl();
      setVideoSrc(null);
      setVideoError(null);
      setVideoLoading(false);
      return undefined;
    }

    let cancelled = false;
    setVideoError(null);
    setVideoLoading(true);
    setVideoSrc(null);
    revokeVideoObjectUrl();

    fetchAdminSessionVideo(videoSessionId, { variant: videoPlaybackVariant })
      .then(async ({ data }) => {
        if (cancelled) return;
        const playable = await prepareRecordingBlob(data);
        if (cancelled) return;
        const url = URL.createObjectURL(playable);
        videoObjectUrlRef.current = url;
        setVideoSrc(url);
      })
      .catch(async (err) => {
        if (cancelled || isAdminSessionExpiredError(err)) return;
        const status = err?.response?.status;
        const detail = await parseApiErrorDetail(err);
        if (status === 404) {
          setVideoError(
            detail ||
              "Vidéo introuvable sur le serveur (fichier absent ou session sans enregistrement).",
          );
        } else {
          setVideoError(detail || "Impossible de lire la vidéo.");
        }
      })
      .finally(() => {
        if (!cancelled) setVideoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [videoSessionId, videoPlaybackVariant, revokeVideoObjectUrl]);

  useEffect(() => () => revokeVideoObjectUrl(), [revokeVideoObjectUrl]);

  const handleDownloadVideo = useCallback(async (session, variant) => {
    const sessionId = session?.session_id || session?.id;
    if (!sessionId) return;
    setDownloadingId(sessionId);
    try {
      await downloadAdminSessionVideo({ ...session, id: sessionId }, variant);
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      const detail = await parseApiErrorDetail(err);
      toast.error(detail || "Impossible de télécharger la vidéo");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleCopyVideoUrl = useCallback(async (session, variant) => {
    const sessionId = session?.session_id || session?.id;
    if (!sessionId) return;
    try {
      await copyAdminSessionVideoUrl(sessionId, variant);
      toast.success("Lien public copié");
    } catch {
      toast.error("Impossible de copier l'URL");
    }
  }, []);

  const handleContactChange = useCallback(async (row, contact) => {
    const accessCodeId = row?.access_code_id;
    if (!accessCodeId) return;
    const previous = row.contact || "";
    const next = contact || "";
    setSessions((prev) =>
      prev.map((s) =>
        s.access_code_id === accessCodeId ? { ...s, contact: next || null } : s,
      ),
    );
    setContactSavingId(accessCodeId);
    try {
      await api.put(`/admin/access-codes/${accessCodeId}/contact`, { contact: next || null });
    } catch (err) {
      setSessions((prev) =>
        prev.map((s) =>
          s.access_code_id === accessCodeId ? { ...s, contact: previous || null } : s,
        ),
      );
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible d'assigner le contact");
    } finally {
      setContactSavingId(null);
    }
  }, []);

  const handleResendWebhook = useCallback(async (session) => {
    const sessionId = session?.session_id || session?.id;
    if (!sessionId || !sessionHasVideo(session)) return;
    setWebhookResendingId(sessionId);
    try {
      const { data } = await api.post(`/admin/sessions/${sessionId}/resend-webhook`);
      setSessions((prev) =>
        prev.map((s) =>
          (s.session_id || s.id) === sessionId
            ? {
                ...s,
                webhook_status: data.webhook_status,
                webhook_error: data.webhook_error,
                webhook_attempts: data.webhook_attempts ?? s.webhook_attempts,
                webhook_at: new Date().toISOString(),
              }
            : s,
        ),
      );
      if (data.webhook_status === "ok") toast.success("Webhook renvoyé");
      else if (data.webhook_status === "skipped") toast.message(data.webhook_error || "Webhook désactivé");
      else toast.error(data.webhook_error || "Échec du webhook");
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible de renvoyer le webhook");
    } finally {
      setWebhookResendingId(null);
    }
  }, []);

  const openVideo = (session) => {
    const sessionId = session.session_id || session.id;
    if (!sessionId || !sessionHasVideo(session)) return;
    setVideoPlaybackVariant(sessionVideoVariant(session));
    setVideoSessionId(sessionId);
  };

  const filteredSessions = useMemo(() => {
    const q = searchQuery.trim().toUpperCase();
    return (Array.isArray(sessions) ? sessions : []).filter((s) => {
      const displayStatus = getSessionDisplayStatus(s);
      if (statusFilter === "new" && displayStatus !== "new") return false;
      if (statusFilter === "finished" && displayStatus !== "finished") return false;
      if (statusFilter === "recording" && displayStatus !== "recording") return false;
      if (statusFilter === "consumed" && displayStatus !== "consumed") return false;
      if (videoFilter === "with_video" && !sessionHasVideo(s)) return false;
      if (videoFilter === "without_video" && sessionHasVideo(s)) return false;
      if (q) {
        const hay = [s.code, s.contact].filter(Boolean).join(" ").toUpperCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sessions, searchQuery, statusFilter, videoFilter]);

  const deleteCode = async (row) => {
    const accessCodeId = row.access_code_id;
    if (!accessCodeId) {
      toast.error("Impossible de supprimer ce code (identifiant manquant)");
      return;
    }
    if (
      !window.confirm(
        `Supprimer définitivement le code ${row.code} ?\n\nLa ligne disparaîtra (code, partie et vidéos). Action irréversible.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/access-codes/${accessCodeId}`);
      if (videoSessionId && (videoSessionId === sessionRowId(row) || videoSessionId === row.access_code_id)) {
        setVideoSessionId(null);
      }
      toast.success(`Code ${row.code} supprimé`);
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible de supprimer ce code");
    }
  };

  const resetSessionCode = async (session) => {
    const hasVideo = sessionHasVideo(session);
    const message = hasVideo
      ? `Réinitialiser le code ${session.code} ?\n\nLa partie en cours sera effacée (vidéo incluse). Statut « Nouveau » : le code sera à nouveau utilisable.`
      : `Réinitialiser le code ${session.code} ?\n\nLa partie sera effacée. Statut « Nouveau » : le code sera à nouveau utilisable.`;
    if (!window.confirm(message)) return;

    const accessCodeId = session.access_code_id;
    if (!accessCodeId || isNewRegistrationRow(session)) {
      toast.error("Aucune partie à réinitialiser pour ce code");
      return;
    }
    try {
      await api.post(`/admin/access-codes/${accessCodeId}/reset`);
      const sessionId = session.session_id || session.id;
      if (videoSessionId === sessionId) setVideoSessionId(null);
      toast.success(`Code ${session.code} réinitialisé`);
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible de réinitialiser le code");
    }
  };

  const openAddDialog = () => {
    setAddCode("");
    setAddDialogOpen(true);
  };

  const submitAddCode = async (e) => {
    e.preventDefault();
    const code = normalizeAccessCode(addCode);
    if (!code) {
      toast.error("Entrez un code d'accès");
      return;
    }
    setAddSaving(true);
    try {
      const { data } = await api.post("/admin/access-codes", { code });
      const created = data?.access_code;
      setAddDialogOpen(false);
      setAddCode("");
      toast.success(`Code ${created?.code || code} ajouté`);
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible d'ajouter ce code");
    } finally {
      setAddSaving(false);
    }
  };

  const filterSelectClass =
    "h-8 rounded-md bg-transparent border border-white/10 px-2.5 text-xs text-white/80 focus:outline-none focus:border-md-cyan/40";

  return (
    <>
      {/* Toolbar type Airtable */}
      <div className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-white/10 bg-[#0a121c]/90 px-3 py-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35" size={14} />
          <Input
            placeholder="Filtrer code, contact…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-xs bg-transparent border-white/10 text-white placeholder:text-white/35"
            data-testid="session-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={filterSelectClass}
          data-testid="session-status-filter"
        >
          <option value="all">Tous les statuts</option>
          <option value="new">Nouveau</option>
          <option value="finished">Terminées</option>
          <option value="recording">En cours</option>
          <option value="consumed">Consommées</option>
        </select>
        <select
          value={videoFilter}
          onChange={(e) => setVideoFilter(e.target.value)}
          className={filterSelectClass}
          data-testid="session-video-filter"
        >
          <option value="all">Toutes les vidéos</option>
          <option value="with_video">Avec vidéo</option>
          <option value="without_video">Sans vidéo</option>
        </select>
        <div className="flex-1" />
        <span className="text-[11px] text-white/40 tabular-nums mr-1">
          {filteredSessions.length}
          {filteredSessions.length !== sessions.length ? ` / ${sessions.length}` : ""} lignes
        </span>
        <Button
          size="sm"
          onClick={openAddDialog}
          className="h-8 bg-md-cyan/90 text-slate-950 hover:bg-md-cyan font-semibold text-xs"
          data-testid="add-access-code-button"
        >
          <Plus size={14} className="mr-1" /> Ajouter
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          className="h-8 text-white/55 hover:text-white text-xs"
          data-testid="refresh-sessions"
        >
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Grille dense */}
      <div className="rounded-lg border border-white/10 overflow-hidden bg-[#080e16]">
        <div className="overflow-x-auto max-h-[min(70vh,720px)] overflow-y-auto">
          <table className="w-full min-w-[980px] border-collapse table-fixed">
            <colgroup>
              <col className="w-[9rem]" />
              <col className="w-[8.5rem]" />
              <col className="w-[8rem]" />
              <col className="w-[5.5rem]" />
              <col className="w-[6.5rem]" />
              <col className="w-[6.5rem]" />
              <col className="w-[5rem]" />
              <col className="w-[3.5rem]" />
              <col className="w-[9rem]" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th className={headCell}>Code</th>
                <th className={headCell}>Contact</th>
                <th className={headCell}>Statut</th>
                <th className={headCell}>Webhook</th>
                <th className={headCell}>Perdue</th>
                <th className={headCell}>Gain</th>
                <th className={headCell}>Q.</th>
                <th className={headCell}>Réinit.</th>
                <th className={headCell}>Démarré</th>
                <th className={headCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-white/40 py-16 text-sm">
                    Aucune ligne ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                filteredSessions.map((s) => {
                  const rowKey = s.access_code_id || s.code || s.id;
                  const ds = getSessionDisplayStatus(s) || "consumed";
                  const lost = formatSessionLostLabel(s);
                  const wh = s.webhook_status;
                  const isHover = hoveredRow === rowKey;
                  return (
                    <tr
                      key={rowKey}
                      className={`group transition-colors ${isHover ? "bg-md-cyan/[0.07]" : "hover:bg-white/[0.03]"}`}
                      onMouseEnter={() => setHoveredRow(rowKey)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className={`${cell} font-mono font-semibold tracking-wider text-white`}>
                        {s.code}
                      </td>
                      <td className={cell}>
                        {(() => {
                          const options = [...teamContacts];
                          const current = String(s.contact || "").trim();
                          if (current && !options.includes(current)) options.unshift(current);
                          return (
                            <select
                              value={current}
                              disabled={!s.access_code_id || contactSavingId === s.access_code_id}
                              onChange={(e) => void handleContactChange(s, e.target.value)}
                              className="h-7 w-full max-w-[7.5rem] rounded bg-transparent border-0 text-xs text-white/85 focus:outline-none focus:ring-1 focus:ring-md-cyan/40 disabled:opacity-50"
                              data-testid={`contact-select-${s.access_code_id || s.code}`}
                            >
                              <option value="">—</option>
                              {options.map((name) => (
                                <option key={name} value={name}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          );
                        })()}
                      </td>
                      <td className={cell}>
                        <span className="inline-flex items-center gap-1.5 text-white/80">
                          <span className={`size-1.5 rounded-full shrink-0 ${STATUS_DOT[ds] || STATUS_DOT.consumed}`} />
                          {SESSION_DISPLAY_LABELS[ds] || "—"}
                        </span>
                      </td>
                      <td className={cell}>
                        {wh ? (
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ${WEBHOOK_PILL[wh] || WEBHOOK_PILL.skipped}`}
                            title={s.webhook_error || undefined}
                            data-testid={`webhook-status-${sessionRowId(s) || s.code}`}
                          >
                            {WEBHOOK_LABELS[wh] || wh}
                            {Number(s.webhook_attempts) > 1 ? ` ·${s.webhook_attempts}` : ""}
                          </span>
                        ) : (
                          <span className="text-white/25">—</span>
                        )}
                      </td>
                      <td className={cell}>
                        {lost === "—" ? (
                          <span className="text-white/30">—</span>
                        ) : lost === "Non" ? (
                          <span className="text-emerald-300/90">Non</span>
                        ) : (
                          <span className="text-amber-300/90">{lost}</span>
                        )}
                      </td>
                      <td className={`${cell} font-semibold text-md-cyan tabular-nums`}>
                        {shouldShowSessionStats(s) ? formatMoney(s.current_bank) : (
                          <span className="text-white/30 font-normal">—</span>
                        )}
                      </td>
                      <td className={`${cell} text-white/60 tabular-nums`}>
                        {shouldShowSessionStats(s)
                          ? `${(s.results || []).length}/${(s.question_ids || []).length || "?"}`
                          : "—"}
                      </td>
                      <td className={`${cell} text-white/55 text-center tabular-nums`}>
                        {getSessionResetCount(s)}
                      </td>
                      <td className={`${cell} text-white/45 text-xs`}>
                        {new Date(s.started_at || s.registered_at).toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className={`${cell} !border-r-0`}>
                        <div
                          className={`flex flex-wrap items-center gap-0.5 transition-opacity ${
                            isHover ? "opacity-100" : "opacity-70 group-hover:opacity-100"
                          }`}
                        >
                          {sessionHasVideo(s) ? (
                            <>
                              <button
                                type="button"
                                onClick={() => openVideo(s)}
                                className="inline-flex items-center gap-1 h-7 px-1.5 rounded text-[11px] text-emerald-300 hover:bg-emerald-500/15"
                                data-testid={`view-video-${s.id}`}
                              >
                                <Video size={12} /> Voir
                              </button>
                              {isAdminRole ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadVideo(s, sessionVideoVariant(s))}
                                    disabled={downloadingId === sessionRowId(s)}
                                    className="inline-flex items-center h-7 px-1.5 rounded text-[11px] text-emerald-300/80 hover:bg-emerald-500/15 disabled:opacity-40"
                                    data-testid={`download-video-${sessionRowId(s)}`}
                                    title="Télécharger"
                                  >
                                    <Download size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleCopyVideoUrl(s, sessionVideoVariant(s))}
                                    className="inline-flex items-center h-7 px-1.5 rounded text-[11px] text-emerald-300/80 hover:bg-emerald-500/15"
                                    data-testid={`copy-video-url-${sessionRowId(s)}`}
                                    title="Copier l'URL"
                                  >
                                    <Link2 size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleResendWebhook(s)}
                                    disabled={webhookResendingId === sessionRowId(s)}
                                    className="inline-flex items-center h-7 px-1.5 rounded text-[11px] text-md-cyan/90 hover:bg-md-cyan/15 disabled:opacity-40"
                                    data-testid={`resend-webhook-${sessionRowId(s)}`}
                                    title={s.webhook_error || "Renvoyer le webhook"}
                                  >
                                    <Send size={12} />
                                  </button>
                                </>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-[10px] text-white/25 px-1">—</span>
                          )}
                          {canResetSessionCode(s) ? (
                            <button
                              type="button"
                              onClick={() => resetSessionCode(s)}
                              className="inline-flex items-center h-7 px-1.5 rounded text-[11px] text-amber-300/80 hover:bg-amber-500/15"
                              data-testid={`reset-code-${s.access_code_id || s.id}`}
                              title="Réinitialiser"
                            >
                              <RotateCcw size={12} />
                            </button>
                          ) : null}
                          {isAdminRole ? (
                            <button
                              type="button"
                              onClick={() => deleteCode(s)}
                              className="inline-flex items-center h-7 px-1.5 rounded text-[11px] text-red-300/70 hover:bg-red-500/15"
                              data-testid={`delete-binome-${s.access_code_id || s.code}`}
                              title="Supprimer"
                            >
                              <Trash2 size={12} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-md-panel border-md-cyan/15 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-bold text-xl">Ajouter un code</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAddCode} className="space-y-4" data-testid="add-access-code-form">
            <div>
              <Label htmlFor="add-code" className="text-white/80">
                Code d&apos;accès <span className="text-md-cyan">*</span>
              </Label>
              <Input
                id="add-code"
                value={addCode}
                onChange={(e) => setAddCode(normalizeAccessCode(e.target.value))}
                maxLength={16}
                autoComplete="off"
                placeholder="Ex. CASTING42"
                required
                className="mt-1 bg-md-deep/60 border-md-cyan/15 text-white tracking-widest uppercase"
                data-testid="add-code-input"
                autoFocus
              />
              <p className="mt-1 text-xs text-white/45">2 à 16 caractères : lettres, chiffres et tirets.</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setAddDialogOpen(false)}
                className="text-white/70 hover:text-white"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={addSaving}
                className="bg-md-cyan/90 text-slate-950 hover:bg-md-cyan font-semibold"
                data-testid="add-code-submit"
              >
                {addSaving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!videoSessionId} onOpenChange={(v) => !v && setVideoSessionId(null)}>
        <DialogContent className="bg-md-panel border-md-cyan/15 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold text-2xl flex items-center gap-2">
              <Clapperboard /> Session {videoSession?.code}
            </DialogTitle>
          </DialogHeader>
          {videoSession && (
            <div className="space-y-4">
              {sessionHasVideo(videoSession) ? (
                <>
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    {videoError ? (
                      <div className="w-full h-full flex items-center justify-center text-white/60 text-sm px-6 text-center">
                        {videoError}
                      </div>
                    ) : videoLoading ? (
                      <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">
                        Chargement de la vidéo…
                      </div>
                    ) : videoSrc ? (
                      <video
                        controls
                        playsInline
                        preload="auto"
                        className="w-full h-full bg-black"
                        src={videoSrc}
                        data-testid="admin-video-player"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">
                        Chargement de la vidéo…
                      </div>
                    )}
                  </div>
                  {isAdminRole ? (
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button"
                        onClick={() => void handleCopyVideoUrl(videoSession, videoPlaybackVariant)}
                        variant="outline"
                        className="h-10 px-4 text-sm inline-flex items-center border-md-cyan/30 text-md-cyan hover:bg-md-cyan/10"
                        data-testid="dialog-copy-video-url"
                      >
                        <Link2 size={16} className="mr-2" />
                        Copier l&apos;URL
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleDownloadVideo(videoSession, videoPlaybackVariant)}
                        disabled={downloadingId === sessionRowId(videoSession)}
                        className="md-btn-solid h-10 px-4 text-sm inline-flex items-center bg-emerald-600 hover:bg-emerald-500"
                        data-testid="dialog-download-video"
                      >
                        <Download size={16} className="mr-2" />{" "}
                        {downloadingId === sessionRowId(videoSession)
                          ? "Téléchargement…"
                          : "Télécharger"}
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-white/50 text-sm text-center py-4">Aucune vidéo pour cette session.</p>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-white/50 uppercase text-xs tracking-widest">Gain final</div>
                  <div className="font-bold text-md-cyan text-lg">
                    {formatMoney(videoSession.current_bank)}
                  </div>
                </div>
                <div>
                  <div className="text-white/50 uppercase text-xs tracking-widest">Questions jouées</div>
                  <div className="text-white">
                    {(videoSession.results || []).length} / {(videoSession.question_ids || []).length}
                  </div>
                </div>
              </div>
              {(videoSession.results || []).length > 0 && (
                <div className="space-y-2">
                  <div className="text-white/50 uppercase text-xs tracking-widest">Détail des mises</div>
                  {(videoSession.results || []).map((r, i) => (
                    <div key={i} className="text-sm text-white/80 bg-white/5 rounded-lg p-3">
                      <div className="font-medium text-white mb-1">
                        Q{i + 1} — {r.question_text || r.question_id}
                      </div>
                      <div>
                        Bonne réponse :{" "}
                        <span className="text-emerald-400 font-bold">{LETTERS[r.correct_index]}</span>
                      </div>
                      <div className="text-white/60 mt-1">
                        Mises : {r.wagers?.map((w, j) => `${LETTERS[j]}=${formatMoney(w)}`).join(" | ")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SessionsTab;
